import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit, Trash2, X, Upload, Loader2 } from "lucide-react";
import AdminLayout from "../../components/AdminLayout";
import { api } from "../../lib/api";

const EMPTY_VARIANT = () => ({ color: "", hex: "#000000", images: [] });

const EMPTY = {
  name: "", slug: "", description: "", price: 1499, mrp: 0,
  images: [], sizes: ["S","M","L","XL"],
  collection: "core", category: "clothing", stock: 100,
  featured: false, is_published: true,
  fabric: "100% heavyweight cotton, 240gsm",
  fit: "Oversized / Drop shoulder",
  print_method: "Hand screen-printed neon ink",
  delivery_duration: "3–7 business days",
  cancellation_policy: "Orders can be cancelled within 24 hours of placement. Once dispatched, cancellations are not accepted. Refunds are processed within 5–7 business days to the original payment method for eligible returns (damaged/wrong item).",
  size_chart: [
    { size: "S", chest: "20", shoulder: "18", length: "27" },
    { size: "M", chest: "22", shoulder: "19", length: "28" },
    { size: "L", chest: "24", shoulder: "20", length: "29" },
    { size: "XL", chest: "26", shoulder: "21", length: "30" },
  ],
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
function absoluteImg(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
}

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [sizesText, setSizesText] = useState("");
  const [colorVariants, setColorVariants] = useState([{ ...EMPTY_VARIANT(), color: "Black" }]);
  const [uploadingColor, setUploadingColor] = useState(null); // index of color currently uploading
  const colorFileRef = useRef();
  const uploadingForColor = useRef(null); // which variant index the next upload belongs to

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...EMPTY, ...p });
    setSizesText(p.sizes.join(","));
    if (p.color_variants?.length > 0) {
      setColorVariants(p.color_variants.map(cv => ({ ...cv, images: cv.images || [] })));
    } else {
      // Legacy: synthesise one variant per color, first color gets all product images
      const legacyColors = p.colors?.length > 0 ? p.colors : ["Black"];
      setColorVariants(legacyColors.map((c, i) => ({
        color: c,
        hex: "#000000",
        images: i === 0 ? (p.images || []) : [],
      })));
    }
  };

  const openCreate = () => {
    setEditing("new");
    setForm(EMPTY);
    setSizesText("S,M,L,XL");
    setColorVariants([{ ...EMPTY_VARIANT(), color: "Black" }]);
  };

  const close = () => setEditing(null);

  // ─── COLOR VARIANT HELPERS ────────────────────────────────────────────────
  const addColorVariant = () => {
    setColorVariants((cv) => [...cv, { ...EMPTY_VARIANT() }]);
  };
  const removeColorVariant = (i) => {
    setColorVariants((cv) => cv.filter((_, idx) => idx !== i));
  };
  const updateVariant = (i, key, val) => {
    setColorVariants((cv) => cv.map((v, idx) => idx === i ? { ...v, [key]: val } : v));
  };
  const removeVariantImage = (variantIdx, imgIdx) => {
    setColorVariants((cv) => cv.map((v, i) =>
      i === variantIdx ? { ...v, images: v.images.filter((_, ii) => ii !== imgIdx) } : v
    ));
  };
  const uploadColorImage = async (variantIdx, file) => {
    if (!file) return;
    setUploadingColor(variantIdx);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setColorVariants((cv) => cv.map((v, i) =>
        i === variantIdx ? { ...v, images: [...(v.images || []), res.data.url] } : v
      ));
      toast.success("IMAGE UPLOADED");
    } catch (err) {
      toast.error("UPLOAD FAILED", { description: err?.response?.data?.detail || "" });
    } finally { setUploadingColor(null); }
  };

  // ─── SAVE ─────────────────────────────────────────────────────────────────
  const save = async () => {
    const validVariants = colorVariants.filter(cv => cv.color.trim());
    const hasImages = validVariants.some(cv => (cv.images || []).length > 0);
    if (!hasImages) { toast.error("Upload at least 1 image for at least 1 color"); return; }

    // primary images = first color's images (backward compat)
    const primaryImages = validVariants[0]?.images || [];

    const payload = {
      ...form,
      price: parseFloat(form.price),
      mrp: parseFloat(form.mrp || 0),
      stock: parseInt(form.stock || 0),
      is_published: !!form.is_published,
      sizes: sizesText.split(",").map((s) => s.trim()).filter(Boolean),
      colors: validVariants.map(cv => cv.color),
      color_variants: validVariants,
      images: primaryImages,
    };
    try {
      if (editing === "new") {
        await api.post("/products", payload);
        toast.success("PRODUCT CREATED");
      } else {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("PRODUCT UPDATED");
      }
      close(); load();
    } catch (err) {
      toast.error("ERROR", { description: err?.response?.data?.detail || "Failed" });
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    await api.delete(`/products/${p.id}`);
    toast.success("DELETED"); load();
  };

  return (
    <AdminLayout title="Products">
      <div className="flex justify-between items-center mb-6">
        <p className="label-mono text-[#71717A]">{products.length} ITEMS IN CATALOG</p>
        <button onClick={openCreate} className="btn-neon" data-testid="add-product-btn">
          <Plus size={16} /> ADD PRODUCT
        </button>
      </div>

      <div className="bg-[#0A0A0A] border border-[#27272A] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-[#27272A]">
            <tr className="label-mono text-[#71717A]">
              <th className="text-left p-4">IMG</th>
              <th className="text-left p-4">NAME</th>
              <th className="text-left p-4">COLORS</th>
              <th className="text-left p-4">PRICE</th>
              <th className="text-left p-4">STOCK</th>
              <th className="text-left p-4">STATUS</th>
              <th className="text-left p-4">NEW STOCK</th>
              <th className="text-right p-4">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => {
              const firstImg = p.color_variants?.[0]?.images?.[0] || p.images?.[0];
              const variants = p.color_variants?.length > 0 ? p.color_variants : (p.colors || []).map(c => ({ color: c, hex: "#000000" }));
              return (
                <tr key={p.id} className="border-b border-[#18181B] hover:bg-[#111]">
                  <td className="p-4">
                    <div className="flex -space-x-1">
                      {variants.slice(0, 3).map((v, vi) => {
                        const img = (p.color_variants?.find(cv => cv.color === v.color)?.images?.[0]) || (vi === 0 ? firstImg : null);
                        return img ? (
                          <img key={vi} src={absoluteImg(img)} alt="" className="w-10 h-10 object-cover border border-[#111]" title={v.color} />
                        ) : (
                          <div key={vi} className="w-10 h-10 border border-[#111]" style={{ backgroundColor: v.hex || "#111" }} title={v.color} />
                        );
                      })}
                    </div>
                  </td>
                  <td className="p-4 font-display font-bold">{p.name}</td>
                  <td className="p-4">
                    <div className="flex gap-1 flex-wrap">
                      {variants.map((v, i) => (
                        <span key={i} className="inline-flex items-center gap-1 label-mono text-[10px] border border-[#27272A] px-2 py-0.5">
                          <span className="w-2.5 h-2.5 inline-block rounded-full border border-[#444]" style={{ backgroundColor: v.hex || "#111" }} />
                          {v.color}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-4 font-mono-cult text-[#CFF048]">₹{p.price.toFixed(0)}</td>
                  <td className="p-4 font-mono-cult">
                    {p.stock === 0 ? (
                      <span className="text-red-400 label-mono" data-testid={`sold-out-${p.slug}`}>SOLD OUT</span>
                    ) : p.stock}
                  </td>
                  <td className="p-4 label-mono">
                    {p.is_published === false ? (
                      <span className="text-[#71717A]" data-testid={`hidden-${p.slug}`}>HIDDEN</span>
                    ) : (
                      <span className="text-[#CFF048]" data-testid={`live-${p.slug}`}>● LIVE</span>
                    )}
                  </td>
                  <td className="p-4">{p.featured ? <span className="text-[#CFF048]">★</span> : "—"}</td>
                  <td className="p-4 text-right">
                    <button onClick={() => openEdit(p)} className="text-[#71717A] hover:text-[#CFF048] mr-3" data-testid={`edit-product-${p.slug}`}><Edit size={16} /></button>
                    <button onClick={() => del(p)} className="text-[#71717A] hover:text-red-500" data-testid={`delete-product-${p.slug}`}><Trash2 size={16} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ─── PRODUCT MODAL ─────────────────────────────────────────────────── */}
      {editing && (
        <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={close}>
          <div
            className="bg-[#0A0A0A] border border-[#27272A] w-full max-w-2xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="product-modal"
          >
            <div className="flex justify-between items-center p-5 border-b border-[#27272A] sticky top-0 bg-[#0A0A0A] z-10">
              <p className="label-mono text-[#CFF048]">// {editing === "new" ? "CREATE" : "EDIT"} PRODUCT</p>
              <button onClick={close}><X size={18} /></button>
            </div>

            <div className="p-5 space-y-4">
              {/* BASICS */}
              <input className="cult-input" placeholder="PRODUCT NAME" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="product-name" />
              <input className="cult-input" placeholder="SLUG (unique, e.g. og-crown-drip)" value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})} data-testid="product-slug" />
              <textarea className="cult-input" rows={3} placeholder="DESCRIPTION" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} data-testid="product-description" />

              <div className="grid grid-cols-2 gap-3">
                <input className="cult-input" type="number" step="1" placeholder="SELLING PRICE (₹)" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} data-testid="product-price" />
                <input className="cult-input" type="number" step="1" placeholder="MRP / STRIKE-THROUGH (₹)" value={form.mrp} onChange={(e) => setForm({...form, mrp: e.target.value})} data-testid="product-mrp" />
              </div>
              <input className="cult-input" type="number" placeholder="STOCK" value={form.stock} onChange={(e) => setForm({...form, stock: e.target.value})} data-testid="product-stock" />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="label-mono text-[#71717A] mb-1 text-[10px]">// CATEGORY</p>
                  <select className="cult-input w-full" value={form.category || "clothing"} onChange={(e) => setForm({...form, category: e.target.value})} data-testid="product-category">
                    <option value="clothing">Clothing</option>
                    <option value="shoes">Shoes</option>
                    <option value="caps">Caps</option>
                  </select>
                </div>
                <div>
                  <p className="label-mono text-[#71717A] mb-1 text-[10px]">// COLLECTION</p>
                  <select className="cult-input w-full" value={form.collection} onChange={(e) => setForm({...form, collection: e.target.value})} data-testid="product-collection">
                    <option value="core">Core</option>
                    <option value="drop-01">Drop 01</option>
                    <option value="limited">Limited</option>
                  </select>
                </div>
              </div>

              {/* PUBLISH TOGGLE */}
              <div className="border border-[#27272A] p-3 flex items-center justify-between bg-[#0A0A0A]" data-testid="publish-toggle-row">
                <div>
                  <p className="label-mono text-white">// PUBLISH ON WEBSITE</p>
                  <p className="text-[11px] text-[#71717A] mt-1">
                    {form.is_published === false ? "HIDDEN — not visible to shoppers." : "LIVE — visible to shoppers."}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.is_published !== false}
                  onClick={() => setForm({...form, is_published: !(form.is_published !== false)})}
                  className={`relative w-14 h-8 border transition-colors ${form.is_published !== false ? "bg-[#CFF048] border-[#CFF048]" : "bg-[#111] border-[#27272A]"}`}
                  data-testid="publish-toggle-btn"
                >
                  <span className={`absolute top-[3px] w-6 h-6 transition-all ${form.is_published !== false ? "left-[26px] bg-black" : "left-[3px] bg-[#71717A]"}`} />
                </button>
              </div>

              {/* ── COLOR VARIANTS WITH IMAGES ────────────────────────────────── */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <p className="label-mono text-[#CFF048]">// COLOR VARIANTS & IMAGES</p>
                  <button
                    type="button"
                    onClick={addColorVariant}
                    className="label-mono text-[#CFF048] hover:underline flex items-center gap-1 text-xs"
                    data-testid="add-color-variant-btn"
                  >
                    <Plus size={12} /> ADD COLOR
                  </button>
                </div>

                <p className="label-mono text-[#52525B] text-[10px] mb-3">
                  // 1 COLOR MINIMUM · MAX 5 IMAGES PER COLOR · FIRST COLOR = DEFAULT PRODUCT IMAGE
                </p>

                <div className="space-y-4">
                  {colorVariants.map((cv, ci) => (
                    <div key={ci} className="border border-[#27272A] p-4 bg-[#060606]" data-testid={`color-variant-${ci}`}>
                      {/* Color name + hex — plain text inputs only */}
                      <div className="flex items-center gap-3 mb-4">
                        <input
                          className="cult-input flex-1"
                          placeholder="COLOR NAME (e.g. Jet Black)"
                          value={cv.color}
                          onChange={(e) => updateVariant(ci, "color", e.target.value)}
                          data-testid={`color-name-${ci}`}
                        />
                        <input
                          className="cult-input w-32 font-mono-cult text-xs"
                          placeholder="HEX (e.g. #000000)"
                          value={cv.hex || "#000000"}
                          onChange={(e) => updateVariant(ci, "hex", e.target.value)}
                          data-testid={`color-hex-text-${ci}`}
                        />
                        {colorVariants.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeColorVariant(ci)}
                            className="text-red-500 hover:text-red-400 flex-shrink-0"
                            data-testid={`remove-color-${ci}`}
                          ><Trash2 size={16} /></button>
                        )}
                      </div>

                      {/* Per-color image grid */}
                      <p className="label-mono text-[#71717A] text-[10px] mb-2">
                        // IMAGES FOR {cv.color || "THIS COLOR"} · {cv.images?.length || 0}/5 UPLOADED
                      </p>
                      <div className="grid grid-cols-5 gap-2">
                        {(cv.images || []).map((img, ii) => (
                          <div key={ii} className="relative aspect-square border border-[#27272A]">
                            <img src={absoluteImg(img)} alt="" className="w-full h-full object-cover" />
                            <button
                              type="button"
                              onClick={() => removeVariantImage(ci, ii)}
                              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-600 text-white flex items-center justify-center z-10"
                              data-testid={`remove-variant-img-${ci}-${ii}`}
                            ><X size={10} /></button>
                            {ii === 0 && (
                              <span className="absolute bottom-0 left-0 right-0 bg-black/70 label-mono text-[8px] text-center py-0.5">MAIN</span>
                            )}
                          </div>
                        ))}
                        {(cv.images?.length || 0) < 5 && (
                          <button
                            type="button"
                            onClick={() => {
                              uploadingForColor.current = ci;
                              colorFileRef.current?.click();
                            }}
                            disabled={uploadingColor === ci}
                            className="aspect-square border-2 border-dashed border-[#27272A] hover:border-[#CFF048] flex flex-col items-center justify-center gap-1 text-[#71717A] hover:text-[#CFF048] transition-colors"
                            data-testid={`upload-color-img-${ci}`}
                          >
                            {uploadingColor === ci ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : (
                              <Upload size={16} />
                            )}
                            <span className="label-mono text-[10px]">
                              {uploadingColor === ci ? "..." : "UPLOAD"}
                            </span>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Hidden shared file input */}
                <input
                  ref={colorFileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    if (uploadingForColor.current !== null) {
                      uploadColorImage(uploadingForColor.current, e.target.files[0]);
                    }
                    e.target.value = "";
                  }}
                />
              </div>

              {/* PRODUCT DETAILS */}
              <input className="cult-input" placeholder="FABRIC (e.g. 100% cotton, 240gsm)" value={form.fabric} onChange={(e) => setForm({...form, fabric: e.target.value})} data-testid="product-fabric" />
              <input className="cult-input" placeholder="FIT (e.g. Oversized)" value={form.fit} onChange={(e) => setForm({...form, fit: e.target.value})} data-testid="product-fit" />
              <input className="cult-input" placeholder="PRINT METHOD" value={form.print_method} onChange={(e) => setForm({...form, print_method: e.target.value})} data-testid="product-print" />

              <div>
                <p className="label-mono text-[#CFF048] mb-2">// DELIVERY & POLICY</p>
                <input className="cult-input mb-3" placeholder="DELIVERY DURATION (e.g. 3–7 business days)" value={form.delivery_duration || ""} onChange={(e) => setForm({...form, delivery_duration: e.target.value})} data-testid="product-delivery-duration" />
                <textarea className="cult-input" rows={4} placeholder="CANCELLATION & REFUND POLICY" value={form.cancellation_policy || ""} onChange={(e) => setForm({...form, cancellation_policy: e.target.value})} data-testid="product-cancellation-policy" />
              </div>

              {/* SIZE CHART */}
              <div>
                <p className="label-mono text-[#CFF048] mb-2">// SIZE CHART (INCHES)</p>
                <div className="border border-[#27272A] overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[#111]">
                      <tr className="label-mono text-[#71717A]">
                        <th className="text-left p-2">SIZE</th><th className="text-left p-2">CHEST</th><th className="text-left p-2">SHOULDER</th><th className="text-left p-2">LENGTH</th><th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(form.size_chart || []).map((row, i) => (
                        <tr key={i} className="border-t border-[#27272A]">
                          {["size","chest","shoulder","length"].map((k) => (
                            <td key={k} className="p-1">
                              <input className="cult-input !py-1.5 !px-2 !text-xs" value={row[k]} onChange={(e) => {
                                const c = [...form.size_chart]; c[i] = { ...c[i], [k]: e.target.value }; setForm({...form, size_chart: c});
                              }} placeholder={k.toUpperCase()} data-testid={`sc-${k}-${i}`} />
                            </td>
                          ))}
                          <td className="p-1">
                            <button type="button" onClick={() => setForm({...form, size_chart: form.size_chart.filter((_, x) => x !== i)})} className="text-[#71717A] hover:text-red-500" data-testid={`sc-remove-${i}`}><X size={14} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <button type="button" onClick={() => setForm({...form, size_chart: [...(form.size_chart || []), { size: "", chest: "", shoulder: "", length: "" }]})} className="w-full py-2 label-mono text-[#CFF048] hover:bg-[#111] border-t border-[#27272A]" data-testid="sc-add-row">
                    + ADD SIZE ROW
                  </button>
                </div>
              </div>

              <input className="cult-input" placeholder="SIZES (comma-separated: S,M,L,XL)" value={sizesText} onChange={(e) => setSizesText(e.target.value)} />
              <label className="flex items-center gap-2 label-mono">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({...form, featured: e.target.checked})} /> NEW STOCK (show ★ NEW badge)
              </label>
            </div>

            <div className="p-5 border-t border-[#27272A] flex justify-end gap-3 sticky bottom-0 bg-[#0A0A0A]">
              <button onClick={close} className="btn-outline">CANCEL</button>
              <button onClick={save} className="btn-neon" data-testid="save-product-btn">SAVE PRODUCT</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
